/**
 * Plan Callouts — automatically surface items the planner should discuss
 * with the client (and items the client should see in the deliverable).
 *
 * Each alert has:
 *   id        — unique key
 *   severity  — "urgent" | "warning" | "watch" | "info"
 *   audience  — "planner" | "client" | "both"
 *   category  — "amt" | "niit" | "qbi" | "deadlines" | "scomp" | "state" |
 *               "carryforwards" | "scenarios" | "rate" | "swing"
 *   title     — short headline
 *   detail    — explanation
 *   action    — optional next-step CTA (planner only)
 *
 * Rules favor being conservative — better to surface a maybe-relevant
 * callout the planner can dismiss than to silently miss an exposure.
 */

const fmt = (n) =>
  (Math.round(parseFloat(n) || 0)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });

function daysBetween(a, b) {
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// PTE deadlines per state (mirrors the strategies.js map; duplicated here so
// alerts can fire without coupling to that file's exact key shape).
const PTE_DEADLINES_BY_STATE = {
  UT: "Timely-filed entity return (incl. extensions)",
  CA: "6/15 — first PTE estimated payment",
  NY: "3/15 — annual election",
  ID: "On or before original due date of entity return",
  CO: "Timely-filed entity return",
  AZ: "Timely-filed entity return",
  NM: "Timely-filed entity return",
};

const PTE_ELIGIBLE_STATES = Object.keys(PTE_DEADLINES_BY_STATE);

export function generateAlerts(plan, result) {
  if (!plan || !result) return [];
  const alerts = [];

  const fed = result.planned?.federal;
  const baseline = result.baseline?.federal;
  const ind = plan.individual || {};
  const status = ind.filingStatus || "single";
  const entities = plan.entities || [];
  const today = new Date();

  // ── AMT ────────────────────────────────────────────────────────────
  if ((fed?.amtTax || 0) > 0) {
    alerts.push({
      id: "amt-fired",
      severity: "warning",
      audience: "both",
      category: "amt",
      title: `AMT applies — $${fmt(fed.amtTax)} above regular tax`,
      detail:
        "Alternative minimum tax kicks in this year. ISO exercise timing, accelerated depreciation, and private-activity bond interest can all push AMTI higher.",
      action:
        "Verify the AMT Adjustments entry on the 1040 against the Form 6251 workpaper before finalizing.",
    });
  } else if (fed?.amtTMT > 0 && fed?.incomeTax > 0) {
    const ratio = fed.amtTMT / fed.incomeTax;
    if (ratio > 0.9 && ratio < 1.0) {
      alerts.push({
        id: "amt-close",
        severity: "watch",
        audience: "planner",
        category: "amt",
        title: "Close to AMT crossover",
        detail: `Tentative minimum tax is ${(ratio * 100).toFixed(0)}% of regular tax. Year-end add-back items (ISO exercises, PAB interest, depreciation method changes) could push the client into AMT.`,
        action:
          "Stress-test with $50–100K of add-back items to see when AMT fires.",
      });
    }
  }

  // ── NIIT ───────────────────────────────────────────────────────────
  if ((fed?.niitTax || 0) > 0) {
    alerts.push({
      id: "niit-fired",
      severity: "info",
      audience: "both",
      category: "niit",
      title: `NIIT applies — $${fmt(fed.niitTax)} at 3.8%`,
      detail:
        "Net investment income tax applies above the MAGI threshold. Confirm any LTCG from active trade-or-business sales is excluded via the LTCG-Excluded-from-NIIT field on the 1040.",
    });
  } else {
    // Within $25K below the MAGI threshold = watch for next year
    const niitThreshold =
      status === "mfj" ? 250000 : status === "mfs" ? 125000 : 200000;
    const magi = fed?.agi || 0;
    if (magi > 0 && magi >= niitThreshold - 25000 && magi <= niitThreshold) {
      alerts.push({
        id: "niit-close",
        severity: "watch",
        audience: "planner",
        category: "niit",
        title: "Near NIIT threshold",
        detail: `MAGI is $${fmt(magi)} — within $${fmt(niitThreshold - magi)} of the $${fmt(niitThreshold)} ${status === "mfj" ? "MFJ" : "filing"} threshold. Income shifts next year may trigger 3.8% NIIT on investment income.`,
        action:
          "Consider income-deferral or Roth-conversion timing across years.",
      });
    }
  }

  // ── QBI overstated risk ────────────────────────────────────────────
  // If any pass-through entity has W-2 wages entered and the QBI override is
  // blank, check whether the simplified 20% × QBI exceeds the wage/UBIA ceiling.
  for (const e of entities) {
    if (e.type === "1120" || e.type === "sched_c") continue;
    const wages = parseFloat(e.qbiWages) || 0;
    const ubia = parseFloat(e.qbiUBIA) || 0;
    if (wages === 0 && ubia === 0) continue;
    // QBI for this entity = the entity's taxable income (simplified)
    const qbiEst = Math.max(0, parseFloat(e.revenue) || 0) -
                   (parseFloat(e.expenses) || 0) -
                   (parseFloat(e.depreciation) || 0) -
                   (parseFloat(e.otherDeductions) || 0);
    const simplified20 = qbiEst * 0.2;
    const wageCeiling = Math.max(wages * 0.5, wages * 0.25 + ubia * 0.025);
    if (
      simplified20 > wageCeiling &&
      simplified20 - wageCeiling > 5000 &&
      (ind.qbiOverride === "" || ind.qbiOverride == null)
    ) {
      alerts.push({
        id: `qbi-overstate-${e.id}`,
        severity: "warning",
        audience: "planner",
        category: "qbi",
        title: `QBI may be overstated for ${e.name}`,
        detail: `Auto-calc shows 20% × QBI ≈ $${fmt(simplified20)}, but the wage/UBIA ceiling for this entity is $${fmt(wageCeiling)}. Difference: $${fmt(simplified20 - wageCeiling)}.`,
        action:
          "Enter a QBI Override on the 1040 to cap at the ceiling, or document why the simplified calc is appropriate.",
      });
    }
  }

  // ── S-corp owner reasonable compensation ───────────────────────────
  for (const e of entities) {
    if (e.type !== "1120-S") continue;
    const rev = parseFloat(e.revenue) || 0;
    const ownerPct = parseFloat(e.ownershipPct) || 0;
    if (rev > 200000 && ownerPct >= 25 && (parseFloat(ind.wages) || 0) === 0) {
      alerts.push({
        id: `scomp-${e.id}`,
        severity: "warning",
        audience: "planner",
        category: "scomp",
        title: `Reasonable compensation check — ${e.name}`,
        detail: `S-corp with $${fmt(rev)} in forecast revenue and ${ownerPct.toFixed(0)}% owner, but no W-2 wages are entered on the 1040. IRS reasonable comp scrutiny applies.`,
        action:
          "Confirm owner W-2 wages will be paid; enter them under the 1040 Wages field.",
      });
    }
  }

  // ── 1065 GP without SE tax flagged ─────────────────────────────────
  for (const e of entities) {
    if (e.type !== "1065") continue;
    if (e.generalPartner) continue;
    const ownerPct = parseFloat(e.ownershipPct) || 0;
    if (ownerPct >= 25) {
      alerts.push({
        id: `gp-check-${e.id}`,
        severity: "watch",
        audience: "planner",
        category: "scomp",
        title: `Partner status check — ${e.name}`,
        detail: `${ownerPct.toFixed(0)}% partner is marked Limited (not GP). If the partner is materially participating, SE tax may apply on their share.`,
        action:
          'Toggle "GP (SE-taxed)" on the entity if the partner is active.',
      });
    }
  }

  // ── High federal effective rate ────────────────────────────────────
  if (fed?.effectiveRate >= 0.32 && fed?.agi > 250000) {
    alerts.push({
      id: "high-effective",
      severity: "watch",
      audience: "planner",
      category: "rate",
      title: `Federal effective rate ${(fed.effectiveRate * 100).toFixed(1)}%`,
      detail:
        "Effective rate is high — look at tier 2/3 strategies for additional drag reduction.",
      action:
        "PTE election, retirement plan upsize, charitable bunching/DAF, R&D credit, real-estate professional, QSBS — pick what fits the facts.",
    });
  }

  // ── Big swing year ─────────────────────────────────────────────────
  const swing = Math.abs(
    (result.planned?.totalTax || 0) - (result.baseline?.totalTax || 0),
  );
  if (swing >= 50000) {
    alerts.push({
      id: "big-swing",
      severity: "watch",
      audience: "both",
      category: "swing",
      title: `Significant tax change vs. baseline — $${fmt(swing)}`,
      detail:
        "The plan moves federal+state tax by enough that the client's estimated payments and withholding probably need to be recalculated.",
      action:
        "Recompute 1040-ES vouchers and W-4 withholding; coordinate with the client's payroll.",
    });
  }

  // ── PTE election opportunity ───────────────────────────────────────
  if (PTE_ELIGIBLE_STATES.includes(plan.state)) {
    const hasPassThrough = entities.some(
      (e) => e.type === "1120-S" || e.type === "1065",
    );
    const anyPteUsed = entities.some(
      (e) => (parseFloat(e.pteCreditUsed) || 0) > 0,
    );
    if (hasPassThrough && !anyPteUsed) {
      alerts.push({
        id: "pte-opportunity",
        severity: "watch",
        audience: "planner",
        category: "state",
        title: `PTE election opportunity (${plan.state})`,
        detail: `Pass-through entity in a PTE-eligible state with no PTE credit entered. Election bypasses the federal $10K SALT cap on entity-level state tax.`,
        action: `Deadline for ${plan.state}: ${PTE_DEADLINES_BY_STATE[plan.state]}.`,
      });
    }
  }

  // ── Year-end deadlines imminent ────────────────────────────────────
  // (Implemented by the deliverable's timeline; here we just count.)
  const taxYear = plan.taxYear || new Date().getFullYear();
  const yearEnd = new Date(taxYear, 11, 31); // Dec 31
  const daysToYearEnd = daysBetween(today, yearEnd);
  if (daysToYearEnd >= 0 && daysToYearEnd <= 60) {
    const yearEndStrats = [];
    for (const e of entities) {
      for (const s of e.strategies || []) {
        if (!s.applied) continue;
        yearEndStrats.push(s);
      }
    }
    for (const s of ind.strategies || []) {
      if (s.applied) yearEndStrats.push(s);
    }
    if (yearEndStrats.length > 0) {
      alerts.push({
        id: "yearend-deadlines",
        severity: daysToYearEnd <= 14 ? "urgent" : "warning",
        audience: "both",
        category: "deadlines",
        title: `Year-end is ${daysToYearEnd} days away`,
        detail: `${yearEndStrats.length} applied strateg${yearEndStrats.length === 1 ? "y" : "ies"} — many require Dec 31 implementation (PTE elections, harvested losses, retirement contributions, charitable transfers).`,
        action:
          "Schedule client call this week to confirm execution of any year-end-only items.",
      });
    }
  }

  // ── Capital loss carryforward not used ─────────────────────────────
  const capLoss = parseFloat(ind.capitalLossCarryforward) || 0;
  const totalCapGains =
    (parseFloat(ind.shortTermGains) || 0) + (parseFloat(ind.longTermGains) || 0);
  if (capLoss > 0 && totalCapGains === 0) {
    alerts.push({
      id: "caploss-unused",
      severity: "info",
      audience: "planner",
      category: "carryforwards",
      title: `Capital loss carryforward — $${fmt(capLoss)}`,
      detail:
        "No capital gains this year. $3K offsets ordinary income; remainder carries forward. Consider if the client should harvest gains to use the carryforward.",
    });
  } else if (capLoss > 0 && totalCapGains > 0 && totalCapGains < capLoss) {
    alerts.push({
      id: "caploss-partial",
      severity: "info",
      audience: "planner",
      category: "carryforwards",
      title: "Capital loss carryforward partially absorbed",
      detail: `$${fmt(totalCapGains)} of gains absorbed; $${fmt(capLoss - totalCapGains)} carries forward (after $3K against ordinary).`,
    });
  }

  // ── Charitable carryforward expiring soon ──────────────────────────
  const charitableCF = parseFloat(ind.charitableCarryforward) || 0;
  if (charitableCF > 0) {
    alerts.push({
      id: "charitable-cf",
      severity: "info",
      audience: "planner",
      category: "carryforwards",
      title: `Charitable carryforward — $${fmt(charitableCF)}`,
      detail:
        "5-year carryforward window applies. Confirm vintage and AGI limit category (60%/30%/20%) before relying on this year's deduction.",
    });
  }

  // ── §163(j) carryforward present ───────────────────────────────────
  const sec163jCF = parseFloat(ind.section163jCarryforward) || 0;
  if (sec163jCF > 0) {
    alerts.push({
      id: "163j-cf",
      severity: "info",
      audience: "planner",
      category: "carryforwards",
      title: `§163(j) interest carryforward — $${fmt(sec163jCF)}`,
      detail:
        "Indefinite carry. Consider real estate professional election or 30% ATI workaround if interest expense is meaningfully limited.",
    });
  }

  // ── Maybes not converted to Yes ────────────────────────────────────
  const scenarioCount = (plan.scenarios || []).length;
  if (scenarioCount > 0) {
    let allMaybeCount = 0;
    const checkStrats = (refs) => {
      for (const ref of refs || []) {
        if (!ref?.applied) continue;
        const sbS = ref.statusByScenario;
        let allMaybe = true;
        for (const sc of plan.scenarios) {
          const st = sbS?.[sc.id] || ref.status;
          if (st === "yes" || st === "committed" || st === "implemented") {
            allMaybe = false;
            break;
          }
        }
        if (allMaybe) allMaybeCount++;
      }
    };
    for (const e of entities) checkStrats(e.strategies);
    checkStrats(ind.strategies);
    if (allMaybeCount >= 3) {
      alerts.push({
        id: "all-maybes",
        severity: "watch",
        audience: "planner",
        category: "scenarios",
        title: `${allMaybeCount} strategies still on the fence`,
        detail: `These strategies are 'Maybe' across every scenario — the client hasn't committed in any path. Either drop them or convert to Yes in at least one scenario.`,
        action:
          "In the next meeting, walk each one and ask: which scenario is this a fit for?",
      });
    }
  }

  // ── Multi-state apportionment mismatch ─────────────────────────────
  for (const e of entities) {
    const apport = e.apportionment || [];
    if (apport.length > 1) {
      const sum = apport.reduce(
        (acc, a) => acc + (parseFloat(a.pct) || 0),
        0,
      );
      if (Math.abs(sum - 100) > 0.5) {
        alerts.push({
          id: `apport-${e.id}`,
          severity: "warning",
          audience: "planner",
          category: "state",
          title: `Apportionment doesn't sum to 100% — ${e.name}`,
          detail: `Current apportionment sums to ${sum.toFixed(1)}%. State tax may be over- or under-stated.`,
          action: "Re-check apportionment factors on the entity card.",
        });
      }
    }
  }

  // ── Large LTCG without NIIT exclusion entered (active T/B sale risk) ─
  const ltcg = parseFloat(ind.longTermGains) || 0;
  const ltcgExcluded = parseFloat(ind.ltcgExcludedFromNIIT) || 0;
  if (ltcg >= 250000 && ltcgExcluded === 0) {
    alerts.push({
      id: "ltcg-niit-check",
      severity: "watch",
      audience: "planner",
      category: "niit",
      title: "Large LTCG — confirm NIIT treatment",
      detail: `Significant LTCG of $${fmt(ltcg)} flowing to the 1040. If any portion is from an active trade-or-business sale (operating business, real estate where client materially participates), it's excluded from NIIT.`,
      action: "Enter the active-T/B portion in the LTCG Excluded from NIIT field.",
    });
  }

  // Sort: urgent → warning → watch → info
  const sevOrder = { urgent: 0, warning: 1, watch: 2, info: 3 };
  alerts.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity]);

  return alerts;
}
